import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  Fragment,
} from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Spin } from 'antd';
import { Input, Radio, Dialog, Popup, PickerView, Toast } from 'antd-mobile';
import { CalendarOutline } from 'antd-mobile-icons';
import { CaretDownOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { CalendarPickerView } from 'antd-mobile';
import toyotaLogo from '../../assets/TOYOTA LOGO - Horizontal.svg';
import carServiceBookingSvg from '../../assets/car-service-booking.svg';
import useLineLiff from '../../hooks/LineLiff';
import styles from './BindingPage.module.scss';
import successSvg from '../../assets/success.svg';
import failureSvg from '../../assets/status-fail.svg';

import {
  fetchBpServiceFactory,
  fetchTwinFactory,
  fetchBpAvailableTime,
  fetchPastBooking,
  fetchMaintainanceAdvice,
  fetchBookingAvailable,
  submitBpBooking,
  // 損傷照片上傳功能暫時隱藏（WAF 過濾中文/檔案上傳問題），日後啟用時取消註解
  // uploadBpDamagePhotos,
  fetchInsuranceClaimUrl,
  StringFormatter,
} from './CarServiceBookingAction';

import './common.scss';
import './CarServiceBookingPaint.scss';

const insuranceCompanies = [
  { value: 'CI00', label: '兆豐產物' },
  { value: 'CN00', label: '南山產物' },
  { value: 'DI00', label: '第一產物' },
  { value: 'DR00', label: '中國信託' },
  { value: 'DT00', label: '國泰世紀' },
  { value: 'GT00', label: '富邦產物' },
  { value: 'HC00', label: '和泰產物' },
  { value: 'MT00', label: '明台產物' },
  { value: 'SK00', label: '新光產物' },
  { value: 'TE00', label: '新安東京' },
  { value: 'TI00', label: '台灣產物' },
  { value: 'TN00', label: '泰安產物' },
  { value: 'TP00', label: '華山產物' },
  { value: 'UL00', label: '旺旺友聯' },
  { value: 'WN00', label: '華南產物' },
];

const isServiceFactory = (locationName) => {
  return !locationName.includes('保修站');
};

// 手機號碼格式：需為 09 開頭，總共 10 碼數字
const PHONE_REGEX = /^09\d{8}$/;

export default function CarServiceBookingPaint() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { liff } = useLineLiff(process.env.REACT_APP_LIFF_BOOKING);
  const availabilityChecked = useRef(false);
  const liffState = searchParams.get('liff.state');
  const carNo = searchParams.get('carNo');
  const sKey = searchParams.get('sKey');

  // 損傷照片上傳功能暫時隱藏（WAF 過濾中文/檔案上傳問題），日後啟用時取消註解
  // const cameraInputRef = useRef(null);
  // const uploadInputRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const [form, setForm] = useState({
    plate: '',
    name: '',
    phone: '',
    serviceType: '',
    city: '',
    district: '',
    center: '',
    date: '',
    time: '',
    payMethod: '',
    insuranceCompany: '',
    hasCaseNo: '',
    caseNo: '',
    claimAction: '',
  });
  // 損傷照片上傳功能暫時隱藏（WAF 過濾中文/檔案上傳問題），日後啟用時取消註解
  // const [images, setImages] = useState([]);
  const [errors, setErrors] = useState({});

  // Popup visibility
  const [calendarVisible, setCalendarVisible] = useState(false);
  const [cityVisible, setCityVisible] = useState(false);
  const [districtVisible, setDistrictVisible] = useState(false);
  const [centerVisible, setCenterVisible] = useState(false);
  const [insuranceVisible, setInsuranceVisible] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [claimWarningVisible, setClaimWarningVisible] = useState(false);
  const [bpInfo, setBpInfo] = useState(null);
  const [completeDialog, setCompleteDialog] = useState({
    visible: false,
    success: false,
    message: '',
    detail: '',
    autoClose: false,
  });
  const [errorDialog, setErrorDialog] = useState('');

  // Temp values for popups
  const [tempCity, setTempCity] = useState('');
  const [tempDistrict, setTempDistrict] = useState('');
  const [tempCenter, setTempCenter] = useState('');
  const [tempDate, setTempDate] = useState('');
  const [tempInsurance, setTempInsurance] = useState('');
  const [selectedCenter, setSelectedCenter] = useState(null);

  // [Available Factory]
  const [fetchingFactory, setFetchingFactory] = useState(false);
  const [serviceFactory, setServiceFactory] = useState(null);
  const [lastBooking, setLastBooking] = useState();
  const [lastAdvice, setLastAdvice] = useState();
  const [lastAdviceFactory, setLastAdviceFactory] = useState();
  const [twinFactory, setTwinFactory] = useState();

  useEffect(() => {
    if (liffState || !liff) return;
    if (liffState || !carNo) return;

    const idToken = liff.getIDToken();
    const fetchingLastBooking = async () => {
      if (!sKey) return;

      try {
        const lastBookingResponse = await fetchPastBooking(
          idToken,
          carNo,
          sKey
        );
        if (lastBookingResponse.success) {
          if (lastBookingResponse.data === null) {
            throw new Error('無法取得上次預約資料');
          }
          setLastBooking(lastBookingResponse.data);
          setForm((prev) => ({
            ...prev,
            name: lastBookingResponse.data?.contact_name || '',
            phone: lastBookingResponse.data?.contact_phone || '',
          }));
        } else {
          throw new Error(lastBookingResponse.message);
        }
      } catch (error) {
        console.error(error);
        setCompleteDialog({
          visible: true,
          success: false,
          message: error.message || '無法取得上次預約資料',
          autoClose: true,
        });
      }
    };
    const fetchingServiceFactory = async () => {
      setFetchingFactory(true);
      try {
        const factoryResponse = await fetchBpServiceFactory(idToken);
        if (factoryResponse.success) {
          const flat = (factoryResponse.data || []).filter(
            (item) => item.is_bp_eva === 'Y'
          );
          const nested = flat.reduce((acc, item) => {
            const city = item.zip_nm || '其他';
            const district = item.district || '其他';
            if (!acc[city]) acc[city] = {};
            if (!acc[city][district]) acc[city][district] = [];
            acc[city][district].push({
              name: item.branch_full_nm,
              dlr_code: item.dlr_cd,
              brnh_code: item.brnh_cd,
              address: item.addr,
              tel: item.tel,
              zip: item.zip_nm,
            });
            return acc;
          }, {});
          setServiceFactory(nested);
        } else {
          throw new Error(factoryResponse.message);
        }
      } catch (error) {
        console.error(error);
        Toast.show({
          content: error.message || '取得服務廠資料失敗',
          icon: 'fail',
        });
      } finally {
        setFetchingFactory(false);
      }
    };
    const fetchingMaintainanceAdvice = async () => {
      try {
        const adviceResponse = await fetchMaintainanceAdvice(idToken, carNo);
        if (adviceResponse.success) {
          setLastAdvice(adviceResponse.data);
        } else {
          console.error(adviceResponse.message);
          throw new Error('無法取得上次回廠資訊');
        }
      } catch (error) {
        console.error(error);
        Toast.show({
          content: error.message || '取得定保紀錄失敗',
          icon: 'fail',
        });
      }
    };

    setForm((prev) => ({ ...prev, plate: carNo }));

    const checkAvailability = async () => {
      if (availabilityChecked.current) return;
      availabilityChecked.current = true;
      const checkingToast = Toast.show({
        content: '確認預約資格中...',
        icon: 'loading',
        duration: 0,
      });
      try {
        const availableResponse = await fetchBookingAvailable(liff.getIDToken(), carNo);
        checkingToast.close();
        if (!availableResponse.success || availableResponse.data?.book_bp !== 'Y') {
          await Dialog.alert({
            content: '您已有相關預約，請至預約紀錄確認相關資訊',
            confirmText: '確定',
          });
          const params = new URLSearchParams();
          params.set('carNo', carNo);
          if (sKey) params.set('sKey', sKey);
          navigate(`/hotai/toyota/car-service-booking/record?${params.toString()}`);
          return;
        }
      } catch (error) {
        checkingToast.close();
        console.error(error);
      }
    };
    checkAvailability();

    fetchingServiceFactory();
    if (sKey) {
      fetchingLastBooking();
    } else {
      fetchingMaintainanceAdvice();
    }
  }, [liffState, carNo, sKey, liff]);

  const findFactoryFromList = useCallback(
    (dlrCode, brnhCode, callback) => {
      if (!serviceFactory) return;

      let stop = false;
      for (const dataLocation of Object.values(serviceFactory)) {
        if (stop) break;
        for (const dataZoom of Object.values(dataLocation)) {
          if (stop) break;
          for (const data of dataZoom) {
            if (stop) break;
            if (data.brnh_code === brnhCode && data.dlr_code === dlrCode) {
              callback(data);
              stop = true;
            }
          }
        }
      }
    },
    [serviceFactory]
  );

  useEffect(() => {
    if (!lastAdvice || !serviceFactory) return;

    findFactoryFromList(lastAdvice.dlr_code, lastAdvice.brnh_code, (data) => {
      setLastAdviceFactory(data);
    });
  }, [findFactoryFromList, lastAdvice, serviceFactory]);

  useEffect(() => {
    if (!serviceFactory) return;
    if (!lastAdvice && !lastBooking) return;

    let dlrCode = '';
    let brnhCode = '';
    if (lastAdvice) {
      dlrCode = lastAdvice.dlr_code;
      brnhCode = lastAdvice.brnh_code;
    } else if (lastBooking) {
      dlrCode = lastBooking.dlr_code;
      brnhCode = lastBooking.brnh_code;
    } else {
      return;
    }

    if (dlrCode.trim() === '' || brnhCode.trim() === '') {
      return;
    }

    const idToken = liff.getIDToken();
    const fetchingTwinFactory = async () => {
      try {
        const twinFactoryResponse = await fetchTwinFactory(
          idToken,
          carNo,
          dlrCode,
          brnhCode
        );
        if (twinFactoryResponse.success) {
          if (
            twinFactoryResponse.data?.dlr_code &&
            twinFactoryResponse.data?.brnh_code
          ) {
            findFactoryFromList(
              twinFactoryResponse.data.dlr_code,
              twinFactoryResponse.data.brnh_code,
              (data) => {
                setTwinFactory(data);
              }
            );
          }
          console.log('查無子保修站選項');
        } else {
          throw new Error(twinFactoryResponse.message);
        }
      } catch (error) {
        console.error(error);
        Toast.show({
          content: error.message || '取得子保修站資料失敗',
          icon: 'fail',
        });
      }
    };

    fetchingTwinFactory();
  }, [
    carNo,
    findFactoryFromList,
    lastAdvice,
    lastBooking,
    liff,
    serviceFactory,
  ]);

  // [Available Time]
  const [fetchingTime, setFetchingTime] = useState(false);
  const [availableTime, setAvailableTime] = useState({});
  useEffect(() => {
    if (!selectedCenter) return;

    const fetchingAvailableTime = async () => {
      setFetchingTime(true);
      setAvailableTime({});
      try {
        const timeResponse = await fetchBpAvailableTime(
          liff.getIDToken(),
          carNo,
          selectedCenter.dlr_code,
          selectedCenter.brnh_code
        );
        if (timeResponse.success) {
          const rawList = timeResponse.data || [];
          const transformed = rawList.reduce((acc, item) => {
            const date = (item.book_date || '').replace(/\//g, '-');
            if (!date) return acc;
            if (!acc[date]) acc[date] = [];
            if (item.time_set) acc[date].push(item.time_set);
            return acc;
          }, {});
          if (Object.keys(transformed).length > 0) {
            setAvailableTime(transformed);
          } else {
            Toast.show({
              content: '該服務廠暫無可預約時段',
              icon: 'fail',
            });
          }
          return;
        } else {
          throw new Error(timeResponse.message);
        }
      } catch (error) {
        console.error(error);
        Toast.show({
          content: error.message || '取得可預約時段失敗',
          icon: 'fail',
        });
      } finally {
        setFetchingTime(false);
      }
    };

    fetchingAvailableTime();
  }, [selectedCenter, liff]);

  // Pre-select popup first option
  useEffect(() => {
    if (insuranceVisible && !form.insuranceCompany && !tempInsurance)
      setTempInsurance(insuranceCompanies[0].value);
  }, [insuranceVisible, form.insuranceCompany, tempInsurance]);

  // Reset date when service location changes
  useEffect(() => {
    setForm((f) => ({ ...f, date: '', time: '' }));
    setTempDate('');
  }, [form.serviceType, form.center]);

  // Reset insurance sub-fields when payMethod changes
  useEffect(() => {
    if (form.payMethod !== '保險') {
      setForm((f) => ({
        ...f,
        insuranceCompany: '',
        hasCaseNo: '',
        caseNo: '',
        claimAction: '',
      }));
    }
  }, [form.payMethod]);

  // Reset caseNo/claimAction when hasCaseNo changes
  useEffect(() => {
    setForm((f) => ({ ...f, caseNo: '', claimAction: '' }));
  }, [form.hasCaseNo]);

  const isDateDisabled =
    !form.serviceType ||
    (form.serviceType === 'custom' && !form.center) ||
    Object.keys(availableTime).length === 0;

  // Image handlers
  // 損傷照片上傳功能暫時隱藏（WAF 過濾中文/檔案上傳問題），日後啟用時取消註解
  // const handleImagesSelected = (e) => {
  //   const files = Array.from(e.target.files || []);
  //   setImages((prev) => {
  //     const combined = [...prev, ...files];
  //     return combined.slice(0, 5);
  //   });
  //   e.target.value = '';
  // };

  // const handleRemoveImage = (idx) => {
  //   setImages((prev) => prev.filter((_, i) => i !== idx));
  // };

  const selectedInsuranceName =
    insuranceCompanies.find((c) => c.value === form.insuranceCompany)?.label ||
    '';

  // [Complete Event]
  const [countdown, setCountdown] = useState(3);
  useEffect(() => {
    if (!liff || !completeDialog.autoClose) return;
    const timer = setInterval(() => {
      setCountdown((prev) => {
        const newCountdown = prev <= 0 ? 0 : prev - 1;
        if (newCountdown <= 0) {
          // 若使用者已被導去出險平台，就不要再呼叫 liff.closeWindow()，
          // 避免把剛開啟的出險平台頁面一併關閉，只需把倒數彈窗收起來即可。
          if (completeDialog.closeOnComplete === false) {
            setCompleteDialog((prev2) => ({ ...prev2, visible: false }));
          } else {
            liff.closeWindow();
          }
          clearInterval(timer);
        }
        return newCountdown;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [completeDialog, liff]);

  const showSuccess = (closeOnComplete = true) => {
    setCountdown(3);
    setCompleteDialog({
      visible: true,
      success: true,
      message: '預約成功',
      detail: '',
      autoClose: true,
      closeOnComplete,
    });
  };

  const handleConfirmBooking = async () => {
    try {
      const checkToast = Toast.show({
        content: '確認預約資格中...',
        icon: 'loading',
        duration: 10000,
      });

      const availableResponse = await fetchBookingAvailable(
        liff.getIDToken(),
        carNo
      );

      checkToast.close();

      if (
        !availableResponse.success ||
        availableResponse.data?.book_bp !== 'Y'
      ) {
        setConfirmVisible(false);
        Toast.show({
          content: '目前無法預約板噴估價，您可能已有預約紀錄',
          icon: 'fail',
        });
        return;
      }

      const bookingToast = Toast.show({
        content: '預約中...',
        icon: 'loading',
        duration: 10000,
      });

      const payload = {
        car_no: carNo,
        dlr_code: selectedCenter.dlr_code,
        brnh_code: selectedCenter.brnh_code,
        date: form.date,
        time_set: form.time,
        contact_name: form.name,
        contact_tel: form.phone,
        insur: form.payMethod === '保險' ? 'Y' : 'A',
        insur_cd: form.payMethod === '保險' ? form.insuranceCompany : '',
        insur_no: form.payMethod === '保險' && form.hasCaseNo === 'yes' ? form.caseNo : '',
        help_type:
          form.payMethod === '保險' && form.hasCaseNo === 'no'
            ? form.claimAction === 'self'
              ? 'A'
              : 'B'
            : '',
      };

      const response = await submitBpBooking(liff.getIDToken(), carNo, payload);
      bookingToast.close();
      setConfirmVisible(false);

      if (!response.success) {
        setCompleteDialog({
          visible: true,
          success: false,
          message: '預約失敗',
          detail: response.message,
          autoClose: false,
        });
        return;
      }

      if (response.data) {
        setBpInfo(response.data);
      }

      // 損傷照片上傳功能暫時隱藏（WAF 過濾中文/檔案上傳問題），日後啟用時取消註解
      // if (images.length > 0 && response.data) {
      //   const uploadToast = Toast.show({
      //     content: '上傳損傷照片中...',
      //     icon: 'loading',
      //     duration: 10000,
      //   });
      //   try {
      //     await uploadBpDamagePhotos(
      //       liff.getIDToken(),
      //       response.data.dlr_code,
      //       response.data.brnh_code,
      //       response.data.bp_eva_no,
      //       images
      //     );
      //     uploadToast.close();
      //   } catch (uploadError) {
      //     uploadToast.close();
      //     console.error('Failed to upload photos:', uploadError);
      //     await Dialog.alert({
      //       content: '損傷照片上傳失敗，請聯繫專人為您服務',
      //       confirmText: '確定',
      //     });
      //     return;
      //   }
      // }

      const goToInsurance =
        form.payMethod === '保險' &&
        form.hasCaseNo === 'no' &&
        form.claimAction === 'self';
      if (goToInsurance) {
        setClaimWarningVisible(true);
      } else {
        showSuccess();
      }
    } catch (error) {
      console.error(error);
      Toast.show({
        content: error.message || '預約失敗',
        icon: 'fail',
      });
    }
  };

  const handleGoToInsurance = async () => {
    setClaimWarningVisible(false);
    try {
      const urlResponse = await fetchInsuranceClaimUrl(
        liff.getIDToken(),
        bpInfo?.dlr_code,
        bpInfo?.brnh_code,
        bpInfo?.bp_eva_no
      );
      if (urlResponse.success && urlResponse.data?.url) {
        window.open(urlResponse.data.url, '_blank');
      } else {
        Toast.show({ content: '無法取得出險平台連結', icon: 'fail' });
      }
    } catch (error) {
      console.error(error);
      Toast.show({ content: '無法取得出險平台連結', icon: 'fail' });
    }
    // 已導去出險平台，這裡不呼叫 liff.closeWindow()，避免把出險平台頁面一併關閉
    showSuccess(false);
  };

  const handleSubmit = () => {
    const newErrors = {};
    if (!form.name) newErrors.name = '必填';
    if (!form.phone) newErrors.phone = '必填';
    else if (!PHONE_REGEX.test(form.phone)) newErrors.phone = '手機號碼格式錯誤';
    if (!form.serviceType) newErrors.serviceType = '必填';
    if (form.serviceType === 'custom') {
      if (!form.city) newErrors.city = '必填';
      if (!form.district) newErrors.district = '必填';
      if (!form.center) newErrors.center = '必填';
    }
    if (!form.date) newErrors.date = '必填';
    if (!form.time) newErrors.time = '必填';
    if (!form.payMethod) newErrors.payMethod = '必填';
    if (form.payMethod === '保險') {
      if (!form.insuranceCompany) newErrors.insuranceCompany = '必填';
      if (!form.hasCaseNo) newErrors.hasCaseNo = '必填';
      if (form.hasCaseNo === 'yes' && !form.caseNo) newErrors.caseNo = '必填';
      if (form.hasCaseNo === 'no' && !form.claimAction)
        newErrors.claimAction = '必填';
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length === 0) {
      setConfirmVisible(true);
    } else {
      setErrorDialog('請填寫所有必填欄位');
    }
  };

  if (!carNo) return <Fragment />;

  return (
    <div className="center-viewport">
      <div className="maintenance-app">
        {/* Toyota Logo */}
        <div className="toyota-header">
          <Link to="/">
            <img className="toyota-logo" src={toyotaLogo} alt="TOYOTA LOGO" />
          </Link>
        </div>

        <div className="main-content">
          {/* Header */}
          <div className="content-header">
            <div className="header-row">
              <div className="header-text">
                <div className="vehicle-number">{form.plate}</div>
                <h1 className="page-title">愛車預約管理</h1>
              </div>
              <div className="header-icons">
                <img src={carServiceBookingSvg} alt="板噴估價icon" />
              </div>
            </div>
            {/* 3 個功能 Tab */}
            <div className="csb-tabs">
              <button
                type="button"
                className="csb-tab-btn"
                onClick={() => {
                  const params = new URLSearchParams();
                  if (carNo) params.set('carNo', carNo);
                  if (sKey) params.set('sKey', sKey);
                  const queryString = params.toString();
                  navigate(
                    queryString
                      ? `/hotai/toyota/car-service-booking?${queryString}`
                      : '/hotai/toyota/car-service-booking'
                  );
                }}
              >
                保養維修
              </button>
              <button type="button" className="csb-tab-btn csb-tab-btn--active">
                板噴估價
              </button>
              <button
                type="button"
                className="csb-tab-btn"
                onClick={() => {
                  const params = new URLSearchParams();
                  if (carNo) params.set('carNo', carNo);
                  if (sKey) params.set('sKey', sKey);
                  const queryString = params.toString();
                  navigate(
                    queryString
                      ? `/hotai/toyota/car-service-booking/record?${queryString}`
                      : '/hotai/toyota/car-service-booking/record'
                  );
                }}
              >
                預約紀錄
              </button>
            </div>
          </div>

          {/* 警告橫幅 */}
          <div className="csb-recall-warning">
            預約回廠板噴估價，須待車主或保險公司確認維修項目後才會排定施工時間
          </div>

          {/* 表單 */}
          <div className="car-service-container">
            {/* 服務項目（純說明） */}
            <div className="form-group">
              <label className="form-label">
                <span className="required">*</span> 服務項目
              </label>
              <p className="paint-service-desc">
                針對外觀損傷車輛進行維修估價，讓車主
                <br />
                或保險公司於維修前確認維修項目及費用
              </p>
            </div>

            {/* 車牌 */}
            <div className="form-group">
              <label className="form-label">
                <span className="required">*</span> 車牌
              </label>
              <div className="custom-input disabled">{form.plate}</div>
            </div>

            {/* 姓名 */}
            <div className="form-group" data-field="name">
              <label className="form-label">
                <span className="required">*</span> 姓名
                {errors.name && (
                  <span className="form-error">{errors.name}</span>
                )}
              </label>
              <Input
                className="custom-input"
                value={form.name}
                onChange={(v) => setForm((f) => ({ ...f, name: v }))}
              />
            </div>

            {/* 手機號碼 */}
            <div className="form-group" data-field="phone">
              <label className="form-label">
                <span className="required">*</span> 手機號碼
                {errors.phone && (
                  <span className="form-error">{errors.phone}</span>
                )}
              </label>
              <Input
                className="custom-input"
                value={form.phone}
                onChange={(v) => setForm((f) => ({ ...f, phone: v }))}
              />
            </div>

            {/* 服務據點 */}
            <div className="form-group" data-field="serviceType">
              <label className="form-label">
                <span className="required">*</span> 服務據點
                {errors.serviceType && (
                  <span className="form-error">{errors.serviceType}</span>
                )}
              </label>
              <Radio.Group
                value={form.serviceType}
                onChange={(v) => {
                  setForm((f) => ({ ...f, serviceType: v }));
                  if (v === 'last') {
                    if (lastBooking) {
                      setSelectedCenter(lastBooking);
                    } else if (lastAdviceFactory) {
                      setSelectedCenter(lastAdviceFactory);
                    }
                  } else {
                    setSelectedCenter(null);
                  }
                }}
                style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
              >
                {/* 變更定保預約 */}
                {lastBooking && (
                  <Radio
                    value="last"
                    className="location-radio-card"
                    style={{ display: 'flex', alignItems: 'flex-start' }}
                  >
                    <div className="location-location-radio-title-row">
                      <span className="location-radio-title">
                        {isServiceFactory(lastBooking.name)
                          ? '上次回廠服務據點'
                          : '上次回廠定保據點子保修站'}
                      </span>
                    </div>
                    <div className="location-desc">
                      {lastBooking.name}
                      <br />
                      {StringFormatter.toHalfWidthDigits(lastBooking.address)}
                    </div>
                  </Radio>
                )}
                {/* 新定保預約 */}
                {lastAdviceFactory && (
                  <Radio
                    value="last"
                    className="location-radio-card"
                    style={{ display: 'flex', alignItems: 'flex-start' }}
                  >
                    <div className="location-location-radio-title-row">
                      <span className="location-radio-title">
                        {isServiceFactory(lastAdviceFactory.name)
                          ? '上次回廠服務據點'
                          : '上次回廠定保據點子保修站'}
                      </span>
                    </div>
                    <div className="location-desc">
                      {lastAdviceFactory.name}
                      <br />
                      {StringFormatter.toHalfWidthDigits(
                        lastAdviceFactory.address
                      )}
                    </div>
                  </Radio>
                )}
                <Radio
                  value="custom"
                  className="location-radio-card"
                  style={{ display: 'flex', alignItems: 'flex-start' }}
                  disabled={!serviceFactory}
                >
                  <div className="location-location-radio-title-row">
                    <span className="location-radio-title">
                      自行選擇服務據點
                      {fetchingFactory && (
                        <Spin size="small" style={{ paddingLeft: 4 }} />
                      )}
                    </span>
                  </div>
                  {form.serviceType === 'custom' && serviceFactory && (
                    <div className="custom-location-group">
                      <div className="form-group" data-field="city">
                        <label className="form-label">
                          縣市
                          {errors.city && (
                            <span className="form-error">{errors.city}</span>
                          )}
                        </label>
                        <div
                          className="custom-location-selector"
                          onClick={() => setCityVisible(true)}
                        >
                          <span>{form.city || '請選擇'}</span>
                          <CaretDownOutlined style={{ fontSize: 16 }} />
                        </div>
                        <Popup
                          visible={cityVisible}
                          onMaskClick={() => setCityVisible(false)}
                          bodyStyle={{
                            borderTopLeftRadius: 20,
                            borderTopRightRadius: 20,
                            minHeight: 260,
                            padding: 0,
                          }}
                        >
                          <div className="custom-popup-picker">
                            <div className="custom-popup-picker-title">
                              選擇縣市
                            </div>
                            <div className="custom-popup-picker-options">
                              <PickerView
                                columns={[
                                  Object.keys(serviceFactory).map((opt) => {
                                    return {
                                      label: opt,
                                      value: opt,
                                    };
                                  }),
                                ]}
                                onChange={(val) => setTempCity(val[0])}
                              />
                            </div>
                            <div className="custom-popup-picker-actions">
                              <button
                                type="button"
                                className="custom-popup-picker-btn cancel"
                                onClick={() => {
                                  setCityVisible(false);
                                  setTempCity('');
                                }}
                              >
                                取消
                              </button>
                              <button
                                type="button"
                                className="custom-popup-picker-btn ok"
                                onClick={() => {
                                  setForm((f) => ({
                                    ...f,
                                    city: tempCity || form.city,
                                    district: '',
                                    center: '',
                                  }));
                                  setCityVisible(false);
                                  setTempCity('');
                                }}
                              >
                                確定
                              </button>
                            </div>
                          </div>
                        </Popup>
                      </div>
                      <div className="form-group" data-field="district">
                        <label className="form-label">
                          區域
                          {errors.district && (
                            <span className="form-error">
                              {errors.district}
                            </span>
                          )}
                        </label>
                        <div
                          className={
                            'custom-location-selector' +
                            (!form.city ? ' disabled' : '')
                          }
                          onClick={() => form.city && setDistrictVisible(true)}
                        >
                          <span>{form.district || '請選擇'}</span>
                          <CaretDownOutlined style={{ fontSize: 16 }} />
                        </div>
                        <Popup
                          visible={districtVisible}
                          onMaskClick={() => setDistrictVisible(false)}
                          bodyStyle={{
                            borderTopLeftRadius: 20,
                            borderTopRightRadius: 20,
                            minHeight: 260,
                            padding: 0,
                          }}
                        >
                          <div className="custom-popup-picker">
                            <div className="custom-popup-picker-title">
                              選擇區域
                            </div>
                            <div className="custom-popup-picker-options">
                              <PickerView
                                columns={
                                  form.city
                                    ? [
                                      Object.keys(
                                        serviceFactory[form.city]
                                      ).map((opt) => {
                                        return {
                                          label: opt,
                                          value: opt,
                                        };
                                      }),
                                    ]
                                    : []
                                }
                                onChange={(val) => setTempDistrict(val[0])}
                              />
                            </div>
                            <div className="custom-popup-picker-actions">
                              <button
                                type="button"
                                className="custom-popup-picker-btn cancel"
                                onClick={() => {
                                  setDistrictVisible(false);
                                  setTempDistrict('');
                                }}
                              >
                                取消
                              </button>
                              <button
                                type="button"
                                className="custom-popup-picker-btn ok"
                                onClick={() => {
                                  setForm((f) => ({
                                    ...f,
                                    district: tempDistrict || form.district,
                                    center: '',
                                  }));
                                  setDistrictVisible(false);
                                  setTempDistrict('');
                                }}
                                disabled={!form.city}
                              >
                                確定
                              </button>
                            </div>
                          </div>
                        </Popup>
                      </div>
                      <div className="form-group" data-field="center">
                        <label className="form-label">
                          服務廠
                          {errors.center && (
                            <span className="form-error">{errors.center}</span>
                          )}
                        </label>
                        <div
                          className={
                            'custom-location-selector' +
                            (!form.district ? ' disabled' : '')
                          }
                          onClick={() =>
                            form.district && setCenterVisible(true)
                          }
                        >
                          <span>{form.center || '請選擇'}</span>
                          <CaretDownOutlined style={{ fontSize: 16 }} />
                        </div>
                        <Popup
                          visible={centerVisible}
                          onMaskClick={() => setCenterVisible(false)}
                          bodyStyle={{
                            borderTopLeftRadius: 20,
                            borderTopRightRadius: 20,
                            minHeight: 260,
                            padding: 0,
                          }}
                        >
                          <div className="custom-popup-picker">
                            <div className="custom-popup-picker-title">
                              選擇服務廠
                            </div>
                            <div className="custom-popup-picker-options">
                              <PickerView
                                columns={
                                  form.district
                                    ? [
                                      serviceFactory[form.city][
                                        form.district
                                      ].map((opt) => {
                                        return {
                                          label: opt.name,
                                          value: opt.name,
                                          ...opt,
                                        };
                                      }),
                                    ]
                                    : []
                                }
                                onChange={(val) => setTempCenter(val[0])}
                              />
                            </div>
                            <div className="custom-popup-picker-actions">
                              <button
                                type="button"
                                className="custom-popup-picker-btn cancel"
                                onClick={() => {
                                  setCenterVisible(false);
                                  setTempCenter('');
                                }}
                              >
                                取消
                              </button>
                              <button
                                type="button"
                                className="custom-popup-picker-btn ok"
                                onClick={() => {
                                  setForm((f) => ({
                                    ...f,
                                    center: tempCenter || form.center,
                                  }));
                                  setSelectedCenter(
                                    serviceFactory[form.city][
                                      form.district
                                    ].find((c) => c.name === tempCenter)
                                  );
                                  setCenterVisible(false);
                                  setTempCenter('');
                                }}
                                disabled={!form.city || !form.district}
                              >
                                確定
                              </button>
                            </div>
                          </div>
                        </Popup>
                        {form.center && selectedCenter && (
                          <div className="location-desc">{`${selectedCenter.zip
                            } ${StringFormatter.toHalfWidthDigits(
                              selectedCenter.address
                            )} `}</div>
                        )}
                      </div>
                    </div>
                  )}
                </Radio>
              </Radio.Group>
            </div>

            {/* 預約日期 */}
            {selectedCenter && (
              <div className="form-group" data-field="date">
                <label className="form-label">
                  <span className="required">*</span> 預約日期
                  {errors.date && (
                    <span className="form-error">{errors.date}</span>
                  )}
                </label>
                <div
                  className={
                    'custom-input date-picker-input' +
                    (isDateDisabled ? ' disabled' : '')
                  }
                  style={
                    isDateDisabled
                      ? {
                        cursor: 'not-allowed',
                        background: '#f2f2f2',
                        color: '#bbb',
                      }
                      : {}
                  }
                  onClick={() => {
                    if (!isDateDisabled) setCalendarVisible(true);
                  }}
                >
                  <span
                    style={{ flex: 1, color: form.date ? '#40474A' : '#bbb' }}
                  >
                    {form.date
                      ? dayjs(form.date).format('YYYY-MM-DD')
                      : '請選擇'}
                  </span>
                  {fetchingTime ? (
                    <Spin size="small" style={{ paddingLeft: 4 }} />
                  ) : (
                    <CalendarOutline />
                  )}
                </div>
                <Popup
                  visible={calendarVisible}
                  onMaskClick={() => setCalendarVisible(false)}
                  bodyStyle={{
                    borderTopLeftRadius: 20,
                    borderTopRightRadius: 20,
                    minHeight: 320,
                    padding: 0,
                  }}
                >
                  <div className="custom-date-popup-picker">
                    <div className="custom-date-popup-picker-title">
                      選擇預約日期
                    </div>
                    <div className="custom-date-popup-picker-options">
                      <CalendarPickerView
                        value={
                          tempDate
                            ? dayjs(tempDate).toDate()
                            : form.date
                              ? dayjs(form.date).toDate()
                              : null
                        }
                        onChange={(val) =>
                          setTempDate(dayjs(val).format('YYYY-MM-DD'))
                        }
                        min={dayjs().add(1, 'day').toDate()}
                        max={dayjs().add(90, 'day').toDate()}
                        shouldDisableDate={(date) =>
                          !availableTime[dayjs(date).format('YYYY-MM-DD')]
                        }
                        renderHeader={null}
                      />
                    </div>
                    <div className="custom-date-popup-picker-actions">
                      <button
                        type="button"
                        className="custom-date-popup-picker-btn cancel"
                        onClick={() => {
                          setCalendarVisible(false);
                          setTempDate('');
                        }}
                      >
                        取消
                      </button>
                      <button
                        type="button"
                        className="custom-date-popup-picker-btn ok"
                        onClick={() => {
                          setForm((f) => ({
                            ...f,
                            date: tempDate || form.date,
                          }));
                          setCalendarVisible(false);
                          setTempDate('');
                        }}
                        disabled={!tempDate && !form.date}
                      >
                        確定
                      </button>
                    </div>
                  </div>
                </Popup>
              </div>
            )}

            {/* 預約時段 */}
            {form.date && (
              <div className="form-group" data-field="time">
                <label className="form-label">
                  <span className="required">*</span> 預約時段
                  {errors.time && (
                    <span className="form-error">{errors.time}</span>
                  )}
                </label>
                <div className="time-select-grid">
                  {availableTime[form.date] && availableTime[form.date].length !== 0 ? (
                    availableTime[form.date].map((opt) => (
                      <div
                        key={opt}
                        className={`time-select-btn${form.time === opt ? ' selected' : ''}`}
                        onClick={() => setForm((f) => ({ ...f, time: opt }))}
                      >
                        {opt}
                      </div>
                    ))
                  ) : (
                    <div className="no-time-available">本日無可預約時段</div>
                  )}
                </div>
              </div>
            )}

            {/* 付費方式 */}
            <div className="form-group" data-field="payMethod">
              <label className="form-label">
                <span className="required">*</span> 付費方式
                {errors.payMethod && (
                  <span className="form-error">{errors.payMethod}</span>
                )}
              </label>
              <Radio.Group
                value={form.payMethod}
                onChange={(v) => setForm((f) => ({ ...f, payMethod: v }))}
                className="wait-radio-group"
              >
                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Radio
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      marginRight: '48px',
                    }}
                    value="自費"
                  >
                    自費
                  </Radio>
                  <Radio
                    style={{ display: 'flex', alignItems: 'flex-start' }}
                    value="保險"
                  >
                    保險
                  </Radio>
                </div>
              </Radio.Group>
            </div>

            {/* 保險資料（付費方式=保險時顯示） */}
            {form.payMethod === '保險' && (
              <div className="paint-insurance-section">
                <div className="paint-insurance-title">保險資料</div>

                {/* 產險公司 */}
                <div className="form-group" data-field="insuranceCompany">
                  <label className="form-label">
                    <span className="required">*</span> 產險公司
                    {errors.insuranceCompany && (
                      <span className="form-error">
                        {errors.insuranceCompany}
                      </span>
                    )}
                  </label>
                  <div
                    className="custom-selector"
                    onClick={() => setInsuranceVisible(true)}
                  >
                    <span
                      style={{
                        color: selectedInsuranceName ? '#40474a' : '#b9c0c2',
                      }}
                    >
                      {selectedInsuranceName || '請選擇'}
                    </span>
                    <CaretDownOutlined style={{ fontSize: 16 }} />
                  </div>
                  <Popup
                    visible={insuranceVisible}
                    onMaskClick={() => setInsuranceVisible(false)}
                    bodyStyle={{
                      borderTopLeftRadius: 20,
                      borderTopRightRadius: 20,
                      minHeight: 260,
                      padding: 0,
                    }}
                  >
                    <div className="custom-popup-picker">
                      <div className="custom-popup-picker-title">
                        選擇產險公司
                      </div>
                      <div className="custom-popup-picker-options">
                        <PickerView
                          columns={[insuranceCompanies]}
                          onChange={(val) => setTempInsurance(val[0])}
                        />
                      </div>
                      <div className="custom-popup-picker-actions">
                        <button
                          type="button"
                          className="custom-popup-picker-btn cancel"
                          onClick={() => {
                            setInsuranceVisible(false);
                            setTempInsurance('');
                          }}
                        >
                          取消
                        </button>
                        <button
                          type="button"
                          className="custom-popup-picker-btn ok"
                          onClick={() => {
                            setForm((f) => ({
                              ...f,
                              insuranceCompany:
                                tempInsurance || form.insuranceCompany,
                            }));
                            setInsuranceVisible(false);
                            setTempInsurance('');
                          }}
                        >
                          確定
                        </button>
                      </div>
                    </div>
                  </Popup>
                </div>

                {/* 是否已有產險公司案號 */}
                <div className="form-group" data-field="hasCaseNo">
                  <label className="form-label">
                    <span className="required">*</span> 是否已有產險公司案號
                    {errors.hasCaseNo && (
                      <span className="form-error">{errors.hasCaseNo}</span>
                    )}
                  </label>
                  <Radio.Group
                    value={form.hasCaseNo}
                    onChange={(v) => setForm((f) => ({ ...f, hasCaseNo: v }))}
                    className="wait-radio-group"
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                      <Radio
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          marginRight: '48px',
                        }}
                        value="yes"
                      >
                        是
                      </Radio>
                      <Radio
                        style={{ display: 'flex', alignItems: 'flex-start' }}
                        value="no"
                      >
                        否
                      </Radio>
                    </div>
                  </Radio.Group>
                </div>

                {/* 有案號 → 出險案號輸入 */}
                {form.hasCaseNo === 'yes' && (
                  <div className="form-group" data-field="caseNo">
                    <p className="paint-hint-text">
                      車輛事故發生後車主向產險公司報備出險，產險公司審核保戶資料後，會提供車主出險案號，以利產險公司後續管理。
                    </p>
                    <label className="form-label">
                      <span className="required">*</span> 出險案號
                      {errors.caseNo && (
                        <span className="form-error">{errors.caseNo}</span>
                      )}
                    </label>
                    <Input
                      className="custom-input"
                      value={form.caseNo}
                      onChange={(v) => setForm((f) => ({ ...f, caseNo: v }))}
                      placeholder="請輸入出險案號"
                    />
                  </div>
                )}

                {/* 無案號 → 出險資料填寫 */}
                {form.hasCaseNo === 'no' && (
                  <div className="form-group" data-field="claimAction">
                    <label className="form-label">
                      <span className="required">*</span> 出險資料填寫
                      {errors.claimAction && (
                        <span className="form-error">{errors.claimAction}</span>
                      )}
                    </label>
                    <p className="paint-hint-text">
                      為加快辦理出險作業，可自行填寫出險資料，以利服務廠事先協助處理，實際進度需視產險公司處理狀況而定。
                    </p>
                    <Radio.Group
                      value={form.claimAction}
                      onChange={(v) =>
                        setForm((f) => ({ ...f, claimAction: v }))
                      }
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                      }}
                    >
                      <Radio value="self">
                        <span className="paint-radio-label">
                          自行填寫出險資料（將導向產險公司出險網頁）
                        </span>
                      </Radio>
                      <Radio value="later">
                        <span className="paint-radio-label">
                          入廠後再辦出險作業
                        </span>
                      </Radio>
                    </Radio.Group>
                  </div>
                )}
              </div>
            )}

            {/* 圖片上傳功能暫時隱藏（WAF 過濾中文/檔案上傳問題），日後啟用時取消註解整塊 form-group */}
            {/* <div className="form-group">
              <label className="form-label">請上傳車輛損傷相片</label>
              <div className="paint-upload-grid">
                {images.map((img, idx) => (
                  <div key={idx} className="paint-upload-thumb">
                    <img
                      src={URL.createObjectURL(img)}
                      alt={`損傷相片${idx + 1}`}
                    />
                    <button
                      type="button"
                      className="paint-upload-remove"
                      onClick={() => handleRemoveImage(idx)}
                    >
                      ×
                    </button>
                  </div>
                ))}
                {images.length < 5 && (
                  <button
                    type="button"
                    className="paint-upload-btn"
                    onClick={() => cameraInputRef.current?.click()}
                  >
                    <span className="paint-upload-icon">📷</span>
                    <span className="paint-upload-text">拍照</span>
                  </button>
                )}
                {images.length < 5 && (
                  <button
                    type="button"
                    className="paint-upload-btn"
                    onClick={() => uploadInputRef.current?.click()}
                  >
                    <span className="paint-upload-icon">＋</span>
                    <span className="paint-upload-text">上傳</span>
                  </button>
                )}
              </div>
              <p className="paint-upload-count">
                已選擇 {images.length} 張（最多選擇5張相片）
              </p>
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: 'none' }}
                onChange={handleImagesSelected}
              />
              <input
                ref={uploadInputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={handleImagesSelected}
              />
            </div> */}
          </div>
        </div>

        {/* 底部送出按鈕 */}
        <div className="bottom-btn-bar">
          <button
            type="button"
            className="csb-submit-btn"
            onClick={handleSubmit}
          >
            確定送出
          </button>
        </div>

        {/* Step 1：確認預約 Dialog */}
        <Dialog
          visible={confirmVisible}
          showCloseButton={false}
          content={
            <div className="paint-dialog">
              <div className="paint-dialog-title">確認預約</div>
              <div className="paint-dialog-body">
                <div className="paint-dialog-row">
                  <span className="paint-dialog-label">預約時間</span>
                  <span className="paint-dialog-value">{form.date}</span>
                </div>
                <div className="paint-dialog-row">
                  <span className="paint-dialog-label">服務據點</span>
                  <span className="paint-dialog-value">
                    {selectedCenter?.name || form.center}
                  </span>
                </div>
              </div>
              <div className="paint-dialog-actions">
                <button
                  className="paint-dialog-btn paint-dialog-btn--cancel"
                  onClick={() => setConfirmVisible(false)}
                >
                  取消
                </button>
                <button
                  className="paint-dialog-btn paint-dialog-btn--ok"
                  onClick={handleConfirmBooking}
                >
                  確定
                </button>
              </div>
            </div>
          }
          actions={[]}
        />

        {/* Step 2：前往出險平台 Dialog（保險+無案號+自行填寫） */}
        <Dialog
          visible={claimWarningVisible}
          showCloseButton={false}
          content={
            <div className="paint-dialog">
              <div className="paint-dialog-title">前往出險平台</div>
              <div className="paint-claim-text">
                <p>
                  預約成功！您即將離開本頁面，前往保險公司出險申請平台辦理相關理賠程序。出險平台之服務內容與個人資料保護規定，將依該平台規範辦理。
                </p>
                <p>
                  請確認您已備妥相關資料(如行照、駕照、事故三聯單等)，再繼續操作，是否前往出險平台？
                </p>
                <p>
                  若稍後再填寫可先按取消，之後再到首頁-預約及查詢前往出險平台。
                </p>
              </div>
              <div className="paint-dialog-actions">
                <button
                  className="paint-dialog-btn paint-dialog-btn--cancel"
                  onClick={() => {
                    setClaimWarningVisible(false);
                    showSuccess();
                  }}
                >
                  取消
                </button>
                <button
                  className="paint-dialog-btn paint-dialog-btn--ok"
                  onClick={handleGoToInsurance}
                >
                  繼續前往
                </button>
              </div>
            </div>
          }
          actions={[]}
        />

        {/* 完成 Dialog */}
        <Dialog
          visible={completeDialog.visible}
          showCloseButton={false}
          bodyStyle={{
            display: 'flex',
            height: completeDialog.autoClose ? '230px' : '320px',
            width: '230px',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto',
            padding: 0,
          }}
          content={
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                width: '100%',
                padding: '8px',
                margin: '0 auto',
                boxSizing: 'border-box',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 1,
                  width: '100%',
                  height: '100%',
                  margin: 'auto',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginBottom: '12px',
                  }}
                >
                  <img
                    src={completeDialog.success ? successSvg : failureSvg}
                    alt="booking status"
                    className={styles['status-icon']}
                    style={{ position: 'relative', zIndex: 1 }}
                  />
                </div>
                <div
                  className={styles['status-title']}
                  style={{
                    width: '100%',
                    wordBreak: 'break-word',
                    overflow: 'hidden',
                    textAlign: 'center',
                  }}
                >
                  {completeDialog.message}
                </div>
                <div
                  className={styles['status-desc']}
                  style={{
                    width: '100%',
                    wordBreak: 'break-word',
                    overflow: 'hidden',
                    textAlign: 'center',
                    marginTop: '8px',
                  }}
                >
                  {completeDialog.autoClose
                    ? `${countdown} 秒後將回到 LINE`
                    : completeDialog.detail}
                </div>
              </div>

              {!completeDialog.autoClose && (
                <div
                  style={{
                    width: '100%',
                    paddingTop: '16px',
                  }}
                >
                  <button
                    style={{
                      backgroundColor: '#ef4444',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      fontSize: 16,
                      fontWeight: 500,
                      width: '100%',
                      padding: '8px 16px',
                      cursor: 'pointer',
                      boxSizing: 'border-box',
                    }}
                    onClick={() => {
                      setCompleteDialog((prev) => ({
                        ...prev,
                        visible: false,
                      }));
                    }}
                  >
                    好的
                  </button>
                </div>
              )}
            </div>
          }
          actions={[]}
        />

        {/* 錯誤彈窗 */}
        <Dialog
          visible={!!errorDialog}
          showCloseButton={false}
          content={
            <div className="confirm-dialog error">
              <div className="confirm-dialog-title">請填寫所有必填欄位</div>
              <div className="confirm-dialog-actions">
                <button
                  className="confirm-dialog-btn ok"
                  onClick={() => {
                    setErrorDialog('');
                    setTimeout(() => {
                      const firstKey = Object.keys(errors)[0];
                      const el = document.querySelector(
                        `[data-field="${firstKey}"]`
                      );
                      if (el)
                        el.scrollIntoView({
                          behavior: 'smooth',
                          block: 'center',
                        });
                    }, 100);
                  }}
                >
                  好的
                </button>
              </div>
            </div>
          }
          actions={[]}
        />
      </div>
    </div>
  );
}
